import React, { useState } from 'react';
import { FileText, ArrowRight, TrendingUp, TrendingDown } from 'lucide-react';
import { FinancialTransaction } from '../../services/database/supabase';
import { aiService } from '../../services/ai';

interface MonthlyReportProps {
  transactions: FinancialTransaction[];
  isBlurred: boolean;
}

interface ReportData {
  totalIncome: number;
  totalExpenses: number;
  balance: number;
  savings: number;
  transactionCount: number;
  topCategories: { category: string; amount: number }[];
  monthOverMonth: {
    incomeChange: number;
    expenseChange: number;
  };
}

export const MonthlyReport: React.FC<MonthlyReportProps> = ({
  transactions,
  isBlurred
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [aiInsight, setAiInsight] = useState<string>('');
  const [showReport, setShowReport] = useState(false);

  const generateReport = async () => {
    setIsLoading(true);
    setShowReport(true);

    try {
      // Calcular dados do relatório
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      
      const monthTransactions = transactions.filter(t => {
        const transactionDate = new Date(t.date.split('T')[0]);
        return transactionDate.getMonth() === currentMonth && 
               transactionDate.getFullYear() === currentYear;
      });

      const lastMonthTransactions = transactions.filter(t => {
        const transactionDate = new Date(t.date.split('T')[0]);
        return transactionDate.getMonth() === (currentMonth - 1) && 
               transactionDate.getFullYear() === currentYear;
      });

      // Calcular totais do mês atual
      const totalIncome = monthTransactions
        .filter(t => t.type === 'Receita' || t.type === 'Salário')
        .reduce((sum, t) => sum + t.amount, 0);

      const totalExpenses = monthTransactions
        .filter(t => t.type === 'Despesas')
        .reduce((sum, t) => sum + t.amount, 0);

      // Calcular totais do mês anterior
      const lastMonthIncome = lastMonthTransactions
        .filter(t => t.type === 'Receita' || t.type === 'Salário')
        .reduce((sum, t) => sum + t.amount, 0);

      const lastMonthExpenses = lastMonthTransactions
        .filter(t => t.type === 'Despesas')
        .reduce((sum, t) => sum + t.amount, 0);

      // Calcular categorias mais comuns
      const categoriesMap = new Map<string, number>();
      monthTransactions.forEach(t => {
        if (t.type === 'Despesas') {
          const current = categoriesMap.get(t.category) || 0;
          categoriesMap.set(t.category, current + t.amount);
        }
      });

      const topCategories = Array.from(categoriesMap.entries())
        .map(([category, amount]) => ({ category, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 3);

      const data: ReportData = {
        totalIncome,
        totalExpenses,
        balance: totalIncome - totalExpenses,
        savings: (totalIncome - totalExpenses) / totalIncome * 100,
        transactionCount: monthTransactions.length,
        topCategories,
        monthOverMonth: {
          incomeChange: ((totalIncome - lastMonthIncome) / lastMonthIncome) * 100,
          expenseChange: ((totalExpenses - lastMonthExpenses) / lastMonthExpenses) * 100
        }
      };

      setReportData(data);

      // Gerar insight com a API do chat
      const prompt = `
        Analise os seguintes dados financeiros do mês e forneça um insight curto e direto (máximo 2 frases) como um especialista financeiro:
        
        Receitas: R$ ${data.totalIncome.toFixed(2)}
        Despesas: R$ ${data.totalExpenses.toFixed(2)}
        Saldo: R$ ${data.balance.toFixed(2)}
        Economia: ${data.savings.toFixed(1)}%
        Variação da Receita: ${data.monthOverMonth.incomeChange.toFixed(1)}%
        Variação da Despesa: ${data.monthOverMonth.expenseChange.toFixed(1)}%
        Principais categorias de gastos: ${data.topCategories.map(c => `${c.category} (R$ ${c.amount.toFixed(2)})`).join(', ')}
      `;

      const response = await aiService.processMessage(prompt);
      setAiInsight(response);

    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
            <FileText className="text-emerald-500" size={20} />
          </div>
          <h3 className="font-medium">Relatório Mensal</h3>
        </div>
        {!showReport && (
          <button 
            onClick={generateReport}
            disabled={isLoading}
            className="px-3 py-1 bg-white/10 rounded-lg text-sm hover:bg-white/20 transition-colors flex items-center gap-2"
          >
            {isLoading ? 'Gerando...' : 'Gerar Relatório'}
            <ArrowRight size={16} />
          </button>
        )}
      </div>

      {showReport && reportData && (
        <div className="space-y-6 animate-fadeIn">
          {/* AI Insight */}
          {aiInsight && (
            <div className="bg-white/5 p-4 rounded-lg border border-white/10">
              <p className="text-sm italic text-white/80">{aiInsight}</p>
            </div>
          )}

          {/* Números principais */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/5 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="text-emerald-500" size={16} />
                <span className="text-sm text-white/60">Receitas</span>
              </div>
              <p className="text-lg font-semibold">
                {isBlurred ? '****' : `R$ ${reportData.totalIncome.toFixed(2)}`}
              </p>
              <span className={`text-xs ${
                reportData.monthOverMonth.incomeChange >= 0 
                  ? 'text-emerald-500' 
                  : 'text-red-500'
              }`}>
                {reportData.monthOverMonth.incomeChange >= 0 ? '↑' : '↓'} 
                {Math.abs(reportData.monthOverMonth.incomeChange).toFixed(1)}%
              </span>
            </div>

            <div className="bg-white/5 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="text-red-500" size={16} />
                <span className="text-sm text-white/60">Despesas</span>
              </div>
              <p className="text-lg font-semibold">
                {isBlurred ? '****' : `R$ ${reportData.totalExpenses.toFixed(2)}`}
              </p>
              <span className={`text-xs ${
                reportData.monthOverMonth.expenseChange <= 0 
                  ? 'text-emerald-500' 
                  : 'text-red-500'
              }`}>
                {reportData.monthOverMonth.expenseChange <= 0 ? '↓' : '↑'} 
                {Math.abs(reportData.monthOverMonth.expenseChange).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* Top Categorias */}
          <div>
            <h4 className="text-sm text-white/60 mb-3">Principais Gastos</h4>
            <div className="space-y-2">
              {reportData.topCategories.map((category, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm">{category.category}</span>
                  <span className="text-sm font-medium">
                    {isBlurred ? '****' : `R$ ${category.amount.toFixed(2)}`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Economia */}
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-sm text-white/60 mb-2">Taxa de Economia</h4>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(0, Math.min(100, reportData.savings))}%` }}
                  />
                </div>
              </div>
              <span className="ml-4 text-sm font-medium">
                {isBlurred ? '**' : `${reportData.savings.toFixed(1)}%`}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
